const ATTRIBUTION_COOKIE_NAME = 'gh_attribution'
const ATTRIBUTION_COOKIE_MAX_AGE_DAYS = 90
const LANDING_URL_MAX_LENGTH = 500

const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id']
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']

export type Touch = {
  referrer?: string
  landing_url?: string
  captured_at?: string
  [param: string]: string | undefined
}

export type StoredAttribution = {
  first_touch?: Touch
  last_touch?: Touch
}

export type AttributionPayload = {
  first_touch?: Touch
  last_touch?: Touch
  fbp?: string
  fbc?: string
  ga_client_id?: string
  page_user_agent?: string
}

// In-memory fallback used when persisting to the cookie is not allowed (consent seam).
let memoryAttribution: StoredAttribution | null = null

const readCookie = (name: string): string | null => {
  const cookies = document.cookie ? document.cookie.split('; ') : []
  for (const cookie of cookies) {
    const separatorIndex = cookie.indexOf('=')
    if (separatorIndex > -1 && cookie.slice(0, separatorIndex) === name) return cookie.slice(separatorIndex + 1)
  }
  return null
}

const writeCookie = (name: string, value: string, maxAgeDays: number): void => {
  const maxAgeSeconds = maxAgeDays * 24 * 60 * 60
  document.cookie = `${name}=${value}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax`
}

const buildTouchFromLocation = (): Touch | null => {
  const searchParams = new URLSearchParams(window.location.search)
  const touch: Touch = {}

  CLICK_ID_PARAMS.concat(UTM_PARAMS).forEach((param) => {
    const value = searchParams.get(param)
    if (value) touch[param] = value
  })

  // Never overwrite with blanks: no click ID and no utm_* param means this landing is not attributed.
  if (Object.keys(touch).length === 0) return null

  if (document.referrer) touch.referrer = document.referrer
  touch.landing_url = `${window.location.pathname}${window.location.search}`.slice(0, LANDING_URL_MAX_LENGTH)
  touch.captured_at = new Date().toISOString()

  return touch
}

export const captureAttribution = (mayPersist = true): void => {
  const touch = buildTouchFromLocation()
  if (!touch) return

  const existing = getAttribution()
  const updated: StoredAttribution = {
    first_touch: existing?.first_touch ?? touch,
    last_touch: touch,
  }

  if (mayPersist)
    writeCookie(ATTRIBUTION_COOKIE_NAME, encodeURIComponent(JSON.stringify(updated)), ATTRIBUTION_COOKIE_MAX_AGE_DAYS)
  else memoryAttribution = updated
}

export const getAttribution = (): StoredAttribution | null => {
  const rawCookie = readCookie(ATTRIBUTION_COOKIE_NAME)
  if (rawCookie) {
    try {
      return JSON.parse(decodeURIComponent(rawCookie))
    } catch {
      return memoryAttribution
    }
  }
  return memoryAttribution
}

const parseGaClientId = (gaCookie: string | null): string | null => {
  // _ga format: GA1.2.XXXXXX.YYYYYY -> client_id is the last two dot-separated segments.
  if (!gaCookie) return null
  const segments = gaCookie.split('.')
  if (segments.length < 4) return null
  return `${segments[segments.length - 2]}.${segments[segments.length - 1]}`
}

const synthesizeFbc = (stored: StoredAttribution | null): string | null => {
  const touch = stored?.last_touch?.fbclid ? stored.last_touch : stored?.first_touch?.fbclid ? stored.first_touch : null
  if (!touch?.fbclid) return null
  const capturedAtMs = touch.captured_at ? new Date(touch.captured_at).getTime() : Date.now()
  return `fb.1.${capturedAtMs}.${touch.fbclid}`
}

export const attributionPayload = (): AttributionPayload | null => {
  const stored = getAttribution()
  const fbp = readCookie('_fbp')
  const fbc = readCookie('_fbc') ?? synthesizeFbc(stored)
  const gaClientId = parseGaClientId(readCookie('_ga'))

  if (!stored && !fbp && !fbc && !gaClientId) return null

  const payload: AttributionPayload = {}
  if (stored?.first_touch) payload.first_touch = stored.first_touch
  if (stored?.last_touch) payload.last_touch = stored.last_touch
  if (fbp) payload.fbp = fbp
  if (fbc) payload.fbc = fbc
  if (gaClientId) payload.ga_client_id = gaClientId
  if (navigator.userAgent) payload.page_user_agent = navigator.userAgent

  return payload
}
